import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { API_URL, getUploadUrl } from '../config/api';
import './css/mainviewcategories.css';

const MainViewCategories = () => {
    const { t, language } = useTranslation();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showFilter, setShowFilter] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('display_order'); // 'name', 'display_order'

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/categories`);
            // Filter only active categories and sort by display_order
            const activeCategories = response.data
                .filter(cat => cat.is_active)
                .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
            setCategories(activeCategories);
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setLoading(false);
        }
    };

    // Transform categories for display with translations
    const transformedCategories = useMemo(() => {
        return categories.map(cat => ({
            id: cat.id,
            name: language === 'ar' ? cat.name_ar : cat.name_en,
            key: cat.key_name,
            image: cat.image ? getUploadUrl(cat.image) : null,
            link: `/${cat.key_name}`,
            display_order: cat.display_order
        }));
    }, [categories, language]);

    // Filter and sort categories
    const filteredCats = useMemo(() => {
        let filtered = [...transformedCategories];

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(cat =>
                cat.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Sort categories
        if (sortBy === 'name') {
            filtered.sort((a, b) => a.name.localeCompare(b.name));
        } else if (sortBy === 'display_order') {
            filtered.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        }

        return filtered;
    }, [transformedCategories, searchTerm, sortBy]);

    if (loading) {
        return (
            <section className='main-view-categories'>
                <div className='container-fluid'>
                    <div className='loading-message'>
                        <p>{t('categories.loading') || 'Loading...'}</p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className='main-view-categories'>
            <div className='container-fluid'>
                <div className='main-view-categories-content'>
                    <div className='categories-header'>
                        <button 
                            className='open-filter-btn'
                            onClick={() => setShowFilter(!showFilter)}
                        >
                            <FunnelIcon className='icon' />
                            <span>{t('categories.filter')}</span>
                        </button>
                    </div>

                    {/* Filter Panel */}
                    {showFilter && (
                        <div className='filter-panel'>
                            <div className='filter-header'>
                                <h3>{t('categories.filter')}</h3>
                                <button 
                                    className='close-filter-btn'
                                    onClick={() => setShowFilter(false)}
                                >
                                    <XMarkIcon className='icon' />
                                </button>
                            </div>
                            
                            <div className='filter-content'>
                                <div className='filter-group'>
                                    <label>{t('categories.search')}</label>
                                    <input
                                        type='text'
                                        placeholder={t('categories.searchPlaceholder')}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className='filter-input'
                                    />
                                </div>

                                <div className='filter-group'>
                                    <label>{t('categories.sortBy') || 'الترتيب حسب'}</label>
                                    <select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        className='filter-select'
                                    >
                                        <option value='name'>{t('categories.sortByName')}</option>
                                        <option value='display_order'>{t('categories.sortByOriginal')}</option>
                                    </select>
                                </div>

                                <div className='filter-results'>
                                    <span>{t('categories.results')}: {filteredCats.length}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="categories-list">
                        <div className='row'>
                            {filteredCats.length > 0 ? (
                                filteredCats.map((cat) => (
                                    <div className='col-lg-6 col-md-6 col-6' key={cat.id}>
                                        <Link to={cat.link}>
                                            <div className='category-item'>
                                                {cat.image ? (
                                                    <img src={cat.image} alt={cat.name} className='category-image' />
                                                ) : (
                                                    <div className='category-image-placeholder'>{cat.name}</div>
                                                )}
                                                <h3>{cat.name}</h3>
                                            </div>
                                        </Link>
                                    </div>
                                ))
                            ) : (
                                <div className='col-6'>
                                    <div className='no-categories'>
                                        <p>{t('categories.noResults')}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Disclaimer */}
                    <div className='calories-disclaimer'>
                      <p>{t('categories.disclaimer')}</p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default MainViewCategories;
